// Dedicated author / E-E-A-T page for Prof Lionel.
// Injects Person JSON-LD, ProfilePage schema and BreadcrumbList so Google and
// Google AI Overview can build a strong entity around the author.
//
// The visible copy pulls from /lib/translations.js (teacher namespace) so the
// text stays in sync with the homepage bio and stays trilingual.

import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { useLanguage } from "@/context/LanguageContext";
import { translations } from "@/lib/translations";
import { BRAND, whatsappLink } from "@/lib/config";
import { useCanonical } from "@/lib/useCanonical";
import { AUTHOR, personSchema } from "@/lib/authorSchema";
import LanguageSwitcher from "@/components/landing/LanguageSwitcher";
import {
  ArrowRight,
  ArrowLeft,
  Instagram,
  MessageCircle,
  Award,
  MapPin,
  Languages,
  BookOpen,
} from "lucide-react";

const UI = {
  pt: {
    back: "Voltar",
    overline: "Sobre o professor · E-E-A-T",
    intro: "Página oficial · autor de todo o conteúdo publicado no npdlbr.com.br.",
    credentialsTitle: "Credenciais & experiência",
    knowsTitle: "Sobre o que ele ensina",
    languagesTitle: "Idiomas",
    ctaTitle: "Marque sua 1ª aula grátis com o Prof Lionel",
    ctaBody:
      "Aula 100% online, 45-60 min, com direito a plano personalizado depois. Sem cartão, sem compromisso.",
    ctaBtn: "Quero minha aula grátis",
    testBtn: "Fazer o teste de nível (5 min)",
    followTitle: "Onde acompanhar",
    reviewedLabel: "Página revisada em",
  },
  en: {
    back: "Back",
    overline: "About the teacher · E-E-A-T",
    intro: "Official page · author of all content published on npdlbr.com.br.",
    credentialsTitle: "Credentials & experience",
    knowsTitle: "What he teaches",
    languagesTitle: "Languages",
    ctaTitle: "Book your 1st free class with Prof Lionel",
    ctaBody:
      "100 % online class, 45-60 min, with a personalised plan afterwards. No card, no commitment.",
    ctaBtn: "Get my free class",
    testBtn: "Take the level test (5 min)",
    followTitle: "Follow along",
    reviewedLabel: "Page reviewed on",
  },
  fr: {
    back: "Retour",
    overline: "À propos du professeur · E-E-A-T",
    intro: "Page officielle · auteur de tout le contenu publié sur npdlbr.com.br.",
    credentialsTitle: "Diplômes & expérience",
    knowsTitle: "Ce qu'il enseigne",
    languagesTitle: "Langues",
    ctaTitle: "Réservez votre 1er cours offert avec Prof Lionel",
    ctaBody:
      "Cours 100 % en ligne, 45-60 min, avec un plan personnalisé ensuite. Sans CB, sans engagement.",
    ctaBtn: "Je veux mon cours offert",
    testBtn: "Faire le test de niveau (5 min)",
    followTitle: "Où me suivre",
    reviewedLabel: "Page révisée le",
  },
};

const REVIEWED_DATE = "2026-02-24"; // bump when the author bio changes

export default function ProfLionel() {
  const { lang } = useLanguage();
  const teacherCopy = (translations[lang] || translations.pt).teacher;
  const ui = UI[lang] || UI.pt;
  useCanonical("/prof-lionel");

  // Inject Person + ProfilePage + BreadcrumbList JSON-LD + <title>/<meta>.
  useEffect(() => {
    const url = "https://npdlbr.com.br/prof-lionel";
    const description = AUTHOR.description;
    const title =
      lang === "fr"
        ? "Prof Lionel · professeur de français natif de Paris"
        : lang === "en"
        ? "Prof Lionel · native Parisian French teacher"
        : "Prof Lionel · professor de francês nativo de Paris";

    const setMeta = (selector, attr, value, content) => {
      let el = document.head.querySelector(selector);
      const created = !el;
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attr, value);
        document.head.appendChild(el);
      } else if (!el.dataset.original) {
        el.dataset.original = el.getAttribute("content") || "";
      }
      el.setAttribute("content", content);
      return { el, created };
    };

    const handles = [
      setMeta('meta[name="description"]', "name", "description", description),
      setMeta('meta[property="og:title"]', "property", "og:title", title),
      setMeta('meta[property="og:description"]', "property", "og:description", description),
      setMeta('meta[property="og:url"]', "property", "og:url", url),
      setMeta('meta[property="og:image"]', "property", "og:image", AUTHOR.image),
      setMeta('meta[property="og:type"]', "property", "og:type", "profile"),
      setMeta('meta[name="twitter:title"]', "name", "twitter:title", title),
      setMeta('meta[name="twitter:description"]', "name", "twitter:description", description),
      setMeta('meta[name="twitter:image"]', "name", "twitter:image", AUTHOR.image),
    ];

    const profilePage = {
      "@context": "https://schema.org",
      "@type": "ProfilePage",
      "@id": `${url}#profile`,
      dateCreated: "2007-01-01",
      dateModified: REVIEWED_DATE,
      mainEntity: personSchema(),
    };

    const breadcrumb = {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Início", item: "https://npdlbr.com.br/" },
        { "@type": "ListItem", position: 2, name: "Prof Lionel", item: url },
      ],
    };

    const s1 = document.createElement("script");
    s1.type = "application/ld+json";
    s1.dataset.dynamic = "prof-lionel-profile";
    s1.textContent = JSON.stringify(profilePage);
    document.head.appendChild(s1);

    const s2 = document.createElement("script");
    s2.type = "application/ld+json";
    s2.dataset.dynamic = "prof-lionel-breadcrumb";
    s2.textContent = JSON.stringify(breadcrumb);
    document.head.appendChild(s2);

    const prevTitle = document.title;
    document.title = title;

    return () => {
      s1.remove();
      s2.remove();
      handles.forEach(({ el, created }) => {
        if (created) {
          el.remove();
        } else if (el.dataset.original !== undefined) {
          el.setAttribute("content", el.dataset.original);
          delete el.dataset.original;
        }
      });
      document.title = prevTitle;
    };
  }, [lang]);

  const reviewedFormatted = new Date(REVIEWED_DATE).toLocaleDateString(
    lang === "en" ? "en-US" : lang === "fr" ? "fr-FR" : "pt-BR",
    { year: "numeric", month: "long", day: "numeric" }
  );

  return (
    <div
      data-testid="prof-lionel-page"
      className="min-h-screen bg-[#f4f1ea] text-[#1e2a5a]"
    >
      <header className="border-b border-[#e5e3db]">
        <div className="max-w-4xl mx-auto px-6 sm:px-10 h-16 sm:h-20 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <span className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-[#1e2a5a] text-[#1e2a5a] font-display italic">
              L
            </span>
            <span className="font-display text-sm sm:text-base">{BRAND.name}</span>
          </Link>
          <div className="flex items-center gap-3">
            <LanguageSwitcher />
            <Link
              to="/"
              className="inline-flex items-center gap-1 text-xs sm:text-sm link-underline"
              data-testid="prof-lionel-back"
            >
              <ArrowLeft className="h-3.5 w-3.5" /> {ui.back}
            </Link>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 sm:px-10 py-12 sm:py-20">
        <p className="text-xs uppercase tracking-[0.22em] text-[#d9381e]">
          {ui.overline}
        </p>

        <div className="mt-6 flex flex-col sm:flex-row gap-8 items-start">
          <img
            src={AUTHOR.image}
            alt={`${AUTHOR.name} — ${AUTHOR.jobTitle}`}
            width="160"
            height="160"
            className="rounded-2xl border border-[#e5e3db] shadow-sm w-40 h-40 object-cover shrink-0"
            data-testid="prof-lionel-photo"
          />
          <div>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05]">
              {teacherCopy.name}
            </h1>
            <p className="mt-4 text-lg text-[#5c5c5c] italic">
              {teacherCopy.title}
            </p>
            <p className="mt-3 text-sm text-[#5c5c5c]">{ui.intro}</p>
          </div>
        </div>

        {/* Bio */}
        <section className="mt-12 space-y-5 post-tldr">
          {teacherCopy.body.map((paragraph) => (
            <p
              key={paragraph.slice(0, 40)}
              className="text-base sm:text-lg leading-[1.75] text-[#3a3a3a]"
            >
              {paragraph}
            </p>
          ))}
        </section>

        {/* Credentials */}
        <section className="mt-16">
          <h2 className="font-display text-2xl sm:text-3xl tracking-tight flex items-center gap-3 post-heading">
            <Award className="h-6 w-6 text-[#d9381e]" />
            {ui.credentialsTitle}
          </h2>
          <ul className="mt-6 space-y-3">
            {AUTHOR.credentials.map((c) => (
              <li key={c} className="flex gap-3 text-base sm:text-lg text-[#3a3a3a]">
                <span className="mt-3 h-1.5 w-1.5 shrink-0 rounded-full bg-[#d9381e]" />
                <span>{c}</span>
              </li>
            ))}
          </ul>
        </section>

        {/* Origin */}
        <section className="mt-14 grid sm:grid-cols-2 gap-6">
          <div className="rounded-xl border border-[#e5e3db] bg-[#f0efea] p-5">
            <p className="text-xs uppercase tracking-[0.18em] text-[#d9381e] flex items-center gap-2">
              <MapPin className="h-3.5 w-3.5" /> Origem · Base
            </p>
            <p className="mt-3 font-display text-lg">Paris, France</p>
            <p className="text-sm text-[#5c5c5c]">
              Rio de Janeiro, Brasil (20 anos)
            </p>
          </div>
          <div className="rounded-xl border border-[#e5e3db] bg-[#f0efea] p-5">
            <p className="text-xs uppercase tracking-[0.18em] text-[#d9381e] flex items-center gap-2">
              <Languages className="h-3.5 w-3.5" /> {ui.languagesTitle}
            </p>
            <p className="mt-3 font-display text-lg">
              {AUTHOR.knowsLanguage.join(" · ")}
            </p>
          </div>
        </section>

        {/* What he teaches */}
        <section className="mt-14">
          <h2 className="font-display text-2xl sm:text-3xl tracking-tight flex items-center gap-3 post-heading">
            <BookOpen className="h-6 w-6 text-[#d9381e]" />
            {ui.knowsTitle}
          </h2>
          <ul className="mt-6 flex flex-wrap gap-2">
            {AUTHOR.knowsAbout.map((k) => (
              <li
                key={k}
                className="rounded-full border border-[#e5e3db] bg-white px-4 py-1.5 text-sm text-[#3a3a3a]"
              >
                {k}
              </li>
            ))}
          </ul>
        </section>

        {/* Social / follow */}
        <section className="mt-14">
          <h2 className="font-display text-2xl sm:text-3xl tracking-tight post-heading">
            {ui.followTitle}
          </h2>
          <div className="mt-6 flex flex-wrap gap-3">
            <a
              href={BRAND.instagram}
              target="_blank"
              rel="me noopener noreferrer"
              data-testid="prof-lionel-instagram"
              className="inline-flex items-center gap-2 rounded-full border border-[#1e2a5a] px-5 py-2.5 text-sm hover:bg-[#1e2a5a] hover:text-[#f4f1ea] transition-colors"
            >
              <Instagram className="h-4 w-4" />
              {BRAND.instagramHandle}
            </a>
            <a
              href={whatsappLink()}
              target="_blank"
              rel="me noopener noreferrer"
              data-testid="prof-lionel-whatsapp"
              className="inline-flex items-center gap-2 rounded-full border border-[#1e2a5a] px-5 py-2.5 text-sm hover:bg-[#1e2a5a] hover:text-[#f4f1ea] transition-colors"
            >
              <MessageCircle className="h-4 w-4" />
              WhatsApp {BRAND.whatsappDisplay}
            </a>
          </div>
        </section>

        {/* CTA */}
        <aside
          className="mt-20 rounded-2xl bg-[#1e2a5a] text-[#f4f1ea] p-8 sm:p-12"
          data-testid="prof-lionel-cta"
        >
          <h2 className="font-display text-3xl sm:text-4xl leading-[1.1] tracking-tight">
            {ui.ctaTitle}
          </h2>
          <p className="mt-4 text-base sm:text-lg text-[#f4f1ea]/85">{ui.ctaBody}</p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Link
              to="/aula-gratis"
              className="group inline-flex items-center gap-2 rounded-full bg-[#f4f1ea] px-6 py-3 text-sm font-medium text-[#1e2a5a] transition-colors hover:bg-[#d9381e] hover:text-[#f4f1ea]"
              data-testid="prof-lionel-cta-book"
            >
              {ui.ctaBtn}
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
            </Link>
            <Link
              to="/teste-de-nivel"
              className="inline-flex items-center gap-2 rounded-full border border-[#f4f1ea]/40 px-6 py-3 text-sm font-medium text-[#f4f1ea] transition-colors hover:border-[#f4f1ea] hover:bg-[#f4f1ea]/10"
              data-testid="prof-lionel-cta-test"
            >
              {ui.testBtn}
            </Link>
          </div>
        </aside>

        <p className="mt-16 text-xs uppercase tracking-[0.18em] text-[#5c5c5c]">
          {ui.reviewedLabel} <time dateTime={REVIEWED_DATE}>{reviewedFormatted}</time>
        </p>
      </main>
    </div>
  );
}
